// frontend-crm/src/pages/SuperAdmin/SuperAdminDashboard.js
import React from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Chip,
  CircularProgress, Skeleton
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  LocalShipping as PedidosIcon,
  TrendingUp as TrendIcon,
  CheckCircle as ActiveIcon,
  Cancel as SuspendedIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { useQuery } from 'react-query';

const API_URL = process.env.REACT_APP_API_URL || '';

const fetchGlobalStats = () =>
  fetch(`${API_URL}/api/superadmin/tenants/stats/global`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json());

const fetchTenants = () =>
  fetch(`${API_URL}/api/superadmin/tenants`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json());

function StatCard({ icon, label, value, color, subtitle, loading }) {
  return (
    <Card
      sx={{
        bgcolor: '#0D1117',
        border: `1px solid ${color}22`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 32px ${color}33` },
      }}
    >
      <Box
        sx={{
          position: 'absolute', top: 0, right: 0,
          width: 120, height: 120,
          background: `radial-gradient(circle at top right, ${color}22, transparent 70%)`,
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              {label}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={60} height={50} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
            ) : (
              <Typography variant="h3" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2, mt: 0.5 }}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: `${color}22`,
              border: `1px solid ${color}44`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function SuperAdminDashboard() {
  const { data: statsRes, isLoading: statsLoading } = useQuery('superadmin-stats', fetchGlobalStats, {
    refetchInterval: 30000
  });
  const { data: tenantsRes, isLoading: tenantsLoading } = useQuery('superadmin-tenants', fetchTenants, {
    refetchInterval: 30000
  });

  const stats = statsRes?.data;
  const tenants = tenantsRes?.data || [];

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mb: 0.5 }}>
          Dashboard SuperAdmin
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
          Vista global de todas las empresas en la plataforma
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BusinessIcon />}
            label="Total Empresas"
            value={stats?.total_empresas ?? '—'}
            color="#6C63FF"
            subtitle={`${stats?.empresas_activas ?? 0} activas`}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleIcon />}
            label="Total Usuarios"
            value={stats?.total_usuarios ?? '—'}
            color="#00D9A5"
            subtitle="En todas las empresas"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PedidosIcon />}
            label="Pedidos Hoy"
            value={stats?.pedidos_hoy ?? '—'}
            color="#FF6B6B"
            subtitle={`${stats?.pedidos_semana ?? 0} esta semana`}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ActiveIcon />}
            label="Ingresos Totales"
            value={stats?.ingresos_totales ? `$${stats.ingresos_totales.toLocaleString()}` : '$0'}
            color="#FFB347"
            subtitle={`$${(stats?.ingresos_hoy ?? 0).toLocaleString()} hoy`}
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Gráfico Pedidos por Día */}
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#fff', mb: 3 }}>
                Pedidos Últimos 7 Días (Global)
              </Typography>
              {statsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: '#6C63FF' }} />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={stats?.grafico_datos || []}>
                    <defs>
                      <linearGradient id="pedidosGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6C63FF" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6C63FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="dia" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ bgcolor: '#1A1F2E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#6C63FF' }}
                    />
                    <Area type="monotone" dataKey="pedidos" stroke="#6C63FF" strokeWidth={2} fill="url(#pedidosGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estado de Empresas */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#0D1117', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#fff', mb: 3 }}>
                Estado de Empresas
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 280, overflow: 'auto' }}>
                {tenantsLoading ? (
                  [1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rounded" height={56} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
                  ))
                ) : (
                  tenants.map(t => (
                    <Box
                      key={t.id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: 1.5, borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                          bgcolor: t.bot_status === 'connected' ? '#00D9A5' : t.bot_status === 'qr-ready' ? '#FFB347' : '#FF6B6B',
                          boxShadow: `0 0 8px ${t.bot_status === 'connected' ? '#00D9A5' : t.bot_status === 'qr-ready' ? '#FFB347' : '#FF6B6B'}88`,
                        }}
                      />
                      <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.nombre}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                          {t.pedidos_hoy} pedidos hoy
                        </Typography>
                      </Box>
                      <Chip
                        label={t.estado}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          bgcolor: t.estado === 'activo' ? 'rgba(0, 217, 165, 0.15)' : 'rgba(255, 107, 107, 0.15)',
                          color: t.estado === 'activo' ? '#00D9A5' : '#FF6B6B',
                          border: `1px solid ${t.estado === 'activo' ? 'rgba(0, 217, 165, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`,
                        }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
